import {
  Reducer,
  useContext,
  useEffect,
  useReducer,
  useState,
  useCallback,
} from "react";
import { ClientTaskType } from "../../types/dtos/tasks/advanced-tasks/ClientTaskType";
import { useTranslation } from "react-i18next";
import SmallLoader from "../../components/loaders/SmallLoader";
import { useAuth } from "react-oidc-context";
import advancedTasksApi from "../../api/task/advancedTasksApi";
import taskTypeIconHelper from "../../helpers/taskTypeIconHelper";
import AppContext from "../../state/AppContext";
import UserContext from "../../state/UserContext";
import { useParams } from "react-router-dom";
import AdvTaskListingApiResponseDto from "../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingApiResponseDto";
import PageIntro from "../../components/common/PageIntro";
import AdvTaskListingDto from "../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingDto";
import AdvTaskListingUserDetailDto from "../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingUserDetailDto";
import AdvTaskListingSearchAndFilter from "../../components/manager-dashboard/adv-task-listing/AdvTaskListingSearchAndFilter";
import { SelectedStatus } from "../../components/manager-dashboard/adv-task-listing/AdvTaskStatusMultiSelect";
import AdvTaskListingStatisticsBar from "../../components/manager-dashboard/adv-task-listing/AdvTaskListingStatisticsBar";
import ListViewToggle from "../../components/manager-dashboard/shared/ListViewToggle";
import AdvTaskUserView from "../../components/manager-dashboard/adv-task-listing/user-view/AdvTaskUserView";
import AdvTaskTableView from "../../components/manager-dashboard/adv-task-listing/table-view/AdvTaskTableView";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpandAlt, faCompressAlt } from "@fortawesome/pro-regular-svg-icons";
import ResultStatus from "../../types/dtos/generic/ResultStatus";
import userApi from "../../api/user/userApi";
import { advancedTaskHelper } from "../../helpers/advancedTaskHelper";

interface ManagerTaskListingPageState {
  isLoading: boolean;
  taskType: ClientTaskType | null;
  headerTitleHtml: string | null;
  headerBodyHtml: string | null;
  users: AdvTaskListingUserDetailDto[];
  tasks: AdvTaskListingDto[];
  originalUsers: AdvTaskListingUserDetailDto[];
  originalTasks: AdvTaskListingDto[];
}

function ManagerTaskListingPage() {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const appContext = useContext(AppContext);
  const auth = useAuth();
  const usersApi = new userApi(auth.user?.access_token);
  const tasksApi = new advancedTasksApi(auth.user?.access_token);

  const { taskTypeId, preselectedEmployeeId } = useParams();

  const [state, setState] = useReducer<
    Reducer<ManagerTaskListingPageState, Partial<ManagerTaskListingPageState>>
  >((state, newState) => ({ ...state, ...newState }), {
    isLoading: false,
    taskType: null,
    headerTitleHtml: null,
    headerBodyHtml: null,
    users: [],
    originalUsers: [],
    tasks: [],
    originalTasks: [],
  });

  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<SelectedStatus[]>(
    [],
  );
  const [selectedCategory, setSelectedCategory] =
    useState<string>("All Categories");
  const [view, setView] = useState<string>("User View");
  const [pageNumber, setPageNumber] = useState(1);
  const [expandAllUsers, setExpandAllUsers] = useState(false);
  const [expandedUserSections, setExpandedUserSections] = useState<string[]>(
    [],
  );
  const [previousView, setPreviousView] = useState<string | null>(null);

  const populateSearchBarWithPreselectedEmployee = (
    users: AdvTaskListingUserDetailDto[],
  ) => {
    const preselectedEmployeeName = users.find(
      (x) => x.employeeId === parseInt(preselectedEmployeeId!),
    )?.employeeFullName;
    if (preselectedEmployeeName != null) {
      setSearchTerm(preselectedEmployeeName);
    }
  };

  useEffect(() => {
    // Reset the expanded user sections (in case user goes from one task page to another)
    // Do NOT remove these else it could cause state exceed limit error.
    setExpandAllUsers(false);
    setExpandedUserSections([]);

    setState({ isLoading: true });
    const clientTaskType = userContext.user.client.taskTypes.find(
      (x) => x.id === taskTypeId,
    );
    if (clientTaskType) {
      appContext.setPageTitle(
        t("Nav.LargeScreen.ManagerDashboard.Root") +
          ": " +
          t(clientTaskType.pluralNameTranslationKeyIdentifier),
      );

      const headerIcon = taskTypeIconHelper.getTaskTypeIcon(
        clientTaskType.iconType,
      );
      appContext.setPageIcon(headerIcon);

      tasksApi.getAdvTaskDataForPeopleManagedByLoggedInUser(
        clientTaskType.id,
        (data: AdvTaskListingApiResponseDto) => {
          if (data) {
            const sortedTasks = advancedTaskHelper.sortTasksForDisplay(
              data.tasks,
            );
            setState({
              isLoading: false,
              taskType: clientTaskType,
              headerTitleHtml: data.headerTitleHtml,
              headerBodyHtml: data.headerBodyHtml,
              users: data.users,
              tasks: sortedTasks,
              originalUsers: data.users,
              originalTasks: sortedTasks,
            });

            setView("User View");

            if (preselectedEmployeeId && data.users.length > 0) {
              populateSearchBarWithPreselectedEmployee(data.users);
            }
          } else {
            // No data, show a blank page
            setState({
              isLoading: false,
              users: [],
              tasks: [],
              originalUsers: [],
              originalTasks: [],
              headerTitleHtml: "",
              headerBodyHtml: "",
            });
          }
        },
        (error: any) => {
          console.error("Error getting tasks", error);
        },
      );
    }
  }, [taskTypeId]);

  useEffect(() => {
    if (state.originalTasks && state.originalTasks.length > 0) {
      applyFilters(
        searchTerm,
        selectedStatuses,
        selectedCategory,
        state.originalTasks,
      );
    }
  }, [selectedStatuses, searchTerm, selectedCategory, state.originalTasks]);

  // Convert display status to the internal status code
  const getStatusCode = (displayStatus: string): string => {
    switch (displayStatus) {
      case "Active":
        return "ACTIVE";
      case "Completed":
        return "COMPLETED";
      case "Cancelled":
        return "CANCELLED";
      case "Historic":
        return "HISTORIC";
      case "Overdue":
        return "OVERDUE";
      default:
        return displayStatus;
    }
  };

  // Extract common filtering logic
  const applyFilters = (
    searchText: string | null,
    statusFilters: SelectedStatus[],
    categoryFilter: string,
    tasksToFilter?: AdvTaskListingDto[],
  ) => {
    // Reset pagination to first page whenever filters change
    setPageNumber(1);

    const originalTasksToUse = tasksToFilter || state.originalTasks;

    if (!originalTasksToUse || !state.originalUsers) return;

    // Get the status codes from the selected statuses
    const selectedStatusCodes = statusFilters.map((s) => s.key);

    // If no statuses are selected, treat it as "all statuses selected"
    const showAllStatuses = selectedStatusCodes.length === 0;

    // Filter tasks by status and category
    const filteredTasks = originalTasksToUse.filter((task) => {
      // First filter by status
      const statusMatches =
        showAllStatuses ||
        (task.overallStatus !== "UNKNOWN" &&
          selectedStatusCodes.includes(task.overallStatus));

      // Then filter by category if selected
      const categoryMatches =
        categoryFilter === "All Categories" ||
        task.categoryId === categoryFilter;

      // Additional filtering for Completed status based on date range
      let dateRangeMatches = true;
      if (task.overallStatus === "COMPLETED" && !showAllStatuses) {
        const completedStatus = statusFilters.find(
          (s) => s.key === "COMPLETED",
        );
        if (completedStatus?.dateRange === "12months") {
          // Filter to last 12 months
          const twelveMonthsAgo = new Date();
          twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
          const taskDate = new Date(task.dateCompleted || task.targetDate);
          dateRangeMatches = taskDate >= twelveMonthsAgo;
        }
        // If dateRange is "alltime", no additional filtering needed
      }

      return statusMatches && categoryMatches && dateRangeMatches;
    });

    // Get all user IDs that have matching tasks
    const userIdsWithMatchingTasks = new Set(
      filteredTasks.map((task) => task.ownerEmployeeId),
    );

    // If no search term, filter users based on whether they have matching tasks
    // or if we're showing all statuses, include users with no tasks as well
    if (!searchText || searchText.trim() === "") {
      let filteredUsers;

      if (
        showAllStatuses || // No statuses selected (show all)
        (statusFilters.length === 5 && categoryFilter === "All Categories") // All 5 statuses explicitly selected
      ) {
        // When showing all statuses and categories, include all users regardless of whether they have tasks
        filteredUsers = state.originalUsers;
      } else {
        // When filtering by status or category, only include users who have tasks matching those criteria
        filteredUsers = state.originalUsers.filter((user) =>
          userIdsWithMatchingTasks.has(user.employeeId),
        );
      }

      setState({
        users: filteredUsers,
        tasks: advancedTaskHelper.sortTasksForDisplay(filteredTasks),
      });
      return;
    }

    // Find users that match the search term
    const usersMatchingSearch = state.originalUsers.filter((user) =>
      user.employeeFullName.toLowerCase().includes(searchText.toLowerCase()),
    );

    // Get IDs of users matching the search
    const userIdsMatchingSearch = new Set(
      usersMatchingSearch.map((user) => user.employeeId),
    );

    // Filter tasks by title (in addition to status and category)
    const tasksMatchingSearch = filteredTasks.filter((task) =>
      task.title.toLowerCase().includes(searchText.toLowerCase()),
    );

    // Get a list of user IDs who have tasks matching the search
    const userIdsWithMatchingSearchTasks = new Set(
      tasksMatchingSearch.map((task) => task.ownerEmployeeId),
    );

    // Combine both sets to get all users we need to display
    const allMatchingUserIds = new Set([
      ...Array.from(userIdsMatchingSearch),
      ...Array.from(userIdsWithMatchingSearchTasks),
    ]);

    // Filter users to include only those matching search or having matching tasks
    const filteredUsers = state.originalUsers.filter((user) =>
      allMatchingUserIds.has(user.employeeId),
    );

    // For the tasks, we need to include:
    // 1. ALL tasks for users whose names match the search
    // 2. Only matching tasks for users whose names don't match the search
    const relevantTasks = filteredTasks.filter((task) => {
      // If this task belongs to a user whose name matches the search, include it
      if (userIdsMatchingSearch.has(task.ownerEmployeeId)) {
        return true;
      }

      // If this task's title matches the search, include it
      if (task.title.toLowerCase().includes(searchText.toLowerCase())) {
        return true;
      }

      // Otherwise, exclude it
      return false;
    });

    setState({
      users: filteredUsers,
      tasks: advancedTaskHelper.sortTasksForDisplay(relevantTasks),
    });
  };

  const handleSearchTermChange = (newSearchTerm: string | null) => {
    // Reset pagination to first page whenever search terms change
    setPageNumber(1);

    setSearchTerm(newSearchTerm);
    applyFilters(newSearchTerm, selectedStatuses, selectedCategory);
  };

  const handleCategoryChange = (newCategory: string) => {
    setSelectedCategory(newCategory);
    applyFilters(searchTerm, selectedStatuses, newCategory);
  };

  // Handle task refresh request from AdvTaskUserView
  const handleTaskRefreshNeeded = (userId: number) => {
    if (!state.taskType) return;

    // Call API to get refreshed tasks for the user
    tasksApi.getAdvTaskDataForUserById(
      userId,
      state.taskType.id,
      (refreshedTasks: AdvTaskListingDto[]) => {
        if (!refreshedTasks.length) return;

        // Create a new originalTasks array with refreshed tasks for this user
        const updatedOriginalTasks = [...state.originalTasks].filter(
          (task) => task.ownerEmployeeId !== userId,
        );
        updatedOriginalTasks.push(...refreshedTasks);

        // Update state with new originalTasks
        setState({
          originalTasks: updatedOriginalTasks,
        });

        // Re-apply filters with the refreshed tasks directly
        applyFilters(
          searchTerm,
          selectedStatuses,
          selectedCategory,
          updatedOriginalTasks,
        );
      },
      (error: any) => {
        console.error("Error refreshing tasks:", error);
      },
    );
  };

  const handleToggleExpandAllUsers = useCallback(() => {
    // If items are manually expanded but expandAllUsers is false, we need to
    // clear expanded sections instead of just toggling expandAllUsers
    if (expandedUserSections.length > 0 && !expandAllUsers) {
      setExpandedUserSections([]);
    } else {
      setExpandAllUsers((prev) => !prev);
    }
  }, [expandAllUsers, expandedUserSections]);

  // Handle clearing notifications for a specific user
  const handleClearNotifications = (userId: number) => {
    if (!state.taskType) return;

    // Find all tasks for this user
    const userTasks = state.tasks.filter(
      (task) => task.ownerEmployeeId === userId,
    );

    // Only proceed if there are highlighted tasks
    if (!userTasks.some((task) => task.highlightToManager)) return;

    tasksApi.clearAllTaskHighlightsForUser(
      state.taskType.id,
      userId,
      (data: ResultStatus) => {
        // Update the original tasks to remove the highlights
        const updatedOriginalTasks = [...state.originalTasks].map((task) => {
          if (task.ownerEmployeeId === userId) {
            return { ...task, highlightToManager: false };
          }
          return task;
        });

        // Update tasks in state
        setState({
          originalTasks: updatedOriginalTasks,
          tasks: state.tasks.map((task) => {
            if (task.ownerEmployeeId === userId) {
              return { ...task, highlightToManager: false };
            }
            return task;
          }),
        });

        // Refresh user details if needed
        usersApi.getRefreshedLoggedInUserDetails(
          userContext.setUpdatedUserDetails,
          () => {},
        );
      },
      (error: any) => {
        console.error("Error clearing notifications", error);
      },
    );
  };

  // Check if a user has any highlighted tasks
  const userHasNotifications = useCallback(
    (userId: number) => {
      return state.tasks.some(
        (task) =>
          task.ownerEmployeeId === userId && task.highlightToManager === true,
      );
    },
    [state.tasks],
  );

  // Handle view toggle with simple reset of expansion states
  const handleViewToggle = useCallback(
    (newView: string) => {
      if (newView === view) return;

      setPreviousView(view);
      setView(newView);

      // Reset expand states when switching views
      setExpandAllUsers(false);
      setExpandedUserSections([]);
    },
    [view],
  );

  // Determine if the button should show "Collapse All"
  const shouldShowCollapseAllUsers =
    expandAllUsers || expandedUserSections.length > 0;

  return (
    <>
      {state.isLoading && (
        <div className="pt-5">
          <SmallLoader />
          <p className="text-center pt-1 text-[#959595] text-sm">
            {t("Common.Loading")}...
          </p>
        </div>
      )}
      {!state.isLoading && state.taskType != null && (
        <div className="px-2 sm:px-4 md:px-6">
          {state.headerTitleHtml && state.headerBodyHtml && (
            <>
              <div>
                <PageIntro
                  title={state.headerTitleHtml}
                  text={state.headerBodyHtml}
                  marginClassNames="mt-2"
                  showIcon={false}
                  collapsible={true}
                  defaultCollapsed={false}
                />
              </div>
            </>
          )}
          <AdvTaskListingStatisticsBar
            tasks={state.tasks}
            originalTasks={state.originalTasks}
            taskType={state.taskType}
            selectedStatuses={selectedStatuses}
            searchTerm={searchTerm}
            onStatusChange={setSelectedStatuses}
            originalUsers={state.originalUsers}
          />
          <AdvTaskListingSearchAndFilter
            onSearch={handleSearchTermChange}
            isLoading={state.isLoading}
            loaderColor="#000"
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            searchTerm={searchTerm}
            taskType={state.taskType}
            selectedCategory={selectedCategory}
            setSelectedCategory={handleCategoryChange}
          />
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3 sm:gap-0">
            <ListViewToggle
              onToggle={handleViewToggle}
              defaultView="User View"
              showUserViewFirst={true}
            />
            {view === "User View" && (
              <div className="flex items-center justify-center sm:justify-end w-full sm:w-auto">
                <button
                  className="btn-secondary px-2 flex items-center text-sm justify-center w-full sm:w-auto"
                  onClick={handleToggleExpandAllUsers}
                  title={
                    shouldShowCollapseAllUsers ? "Collapse All" : "Expand All"
                  }
                >
                  <FontAwesomeIcon
                    icon={
                      shouldShowCollapseAllUsers ? faCompressAlt : faExpandAlt
                    }
                    className="mr-2"
                  />
                  {shouldShowCollapseAllUsers ? "Collapse All" : "Expand All"}
                </button>
              </div>
            )}
          </div>
          {view === "User View" ? (
            <AdvTaskUserView
              users={state.users}
              tasks={state.tasks}
              originalTasks={state.originalTasks}
              taskType={state.taskType}
              onTaskRefreshNeeded={handleTaskRefreshNeeded}
              expandAll={expandAllUsers}
              onExpandedSectionsChange={setExpandedUserSections}
              expandedSections={expandedUserSections}
              key="user-view"
              onClearNotifications={handleClearNotifications}
              userHasNotifications={userHasNotifications}
            />
          ) : (
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <AdvTaskTableView
                users={state.users}
                tasks={state.tasks}
                taskType={state.taskType}
                onTaskRefreshNeeded={handleTaskRefreshNeeded}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                key="table-view"
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default ManagerTaskListingPage;
